import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Alert,
  useTheme,
  Skeleton,
  Divider,
  Tabs,
  Tab,
  Switch,
  Paper,
  InputAdornment
} from "@mui/material";

import {
  CameraAltOutlined,
  SaveOutlined,
  ExitToAppOutlined,
  PersonOutline,
  PhoneIphone,
  EmailOutlined,
  LockOutlined,
  NotificationsNone
} from "@mui/icons-material";

import {
  getAuth,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword
} from "firebase/auth";

import User from "../Services/User";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { storage, db, auth } from "../Firebase/Config";

const UserProfile = () => {

  const theme = useTheme();

  const [tab, setTab] = useState(0);

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [message, setMessage] = useState({ type: "", text: "" });

  const [selectedFile, setSelectedFile] = useState(null);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    num: ""
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdatePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match" });
      return;
    }

    try {

      const user = auth.currentUser;

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );

      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, passwordData.newPassword);

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });

      setMessage({ type: "success", text: "Password updated successfully" });

    } catch (error) {

      let errorMessage = "Error updating password";

      if (error.code === "auth/wrong-password")
        errorMessage = "Current password incorrect";

      if (error.code === "auth/weak-password")
        errorMessage = "Password must be at least 6 characters";

      setMessage({ type: "error", text: errorMessage });
    }
  };

  useEffect(() => {

    const fetchUserData = async () => {

      try {

        const storedUser = JSON.parse(localStorage.getItem("user"));

        if (storedUser?.uid) {

          const data = await User.getUserData(storedUser.uid);

          setUserData(data);

          setFormData({
            nom: data.nom || "",
            prenom: data.prenom || "",
            num: data.num || ""
          });
        }

        setLoading(false);

      } catch (error) {

        setMessage({ type: "error", text: "Failed to load user data" });

        setLoading(false);
      }
    };

    fetchUserData();

  }, []);

  const handleInputChange = (e) => {

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdate = async () => {

    try {

      const storedUser = JSON.parse(localStorage.getItem("user"));

      if (!storedUser?.uid) return;

      const updatedData = { ...formData };

      if (selectedFile) {

        const storageRef = ref(storage, `profile_pics/${storedUser.uid}`);

        await uploadBytes(storageRef, selectedFile);

        const downloadURL = await getDownloadURL(storageRef);

        updatedData.profilePic = downloadURL;
      }

      const userRef = doc(db, "users", storedUser.uid);

      await setDoc(userRef, updatedData, { merge: true });

      const updatedUser = { ...storedUser, ...updatedData };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      setUserData(updatedUser);

      setMessage({
        type: "success",
        text: "Profile updated successfully"
      });

    } catch (error) {

      setMessage({
        type: "error",
        text: "Error updating profile"
      });
    }
  };

  const handleLogout = async () => {

    await User.logout();

    window.location.href = "/login";
  };

  if (loading)
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", p: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );

  return (

    <Box sx={{ maxWidth: 1300, mx: "auto", p: 4 }}>

      <Typography variant="h4" fontWeight="bold" mb={1}>
        Mon Profil
      </Typography>

      <Typography color="text.secondary" mb={4}>
        Gérez vos informations personnelles et paramètres
      </Typography>

      <Grid container spacing={4}>

        {/* LEFT PROFILE CARD */}

        <Grid item xs={12} md={4}>

          <Card sx={{ borderRadius: 4 }}>

            <CardContent sx={{ textAlign: "center" }}>

              <Avatar
                sx={{
                  width: 140,
                  height: 140,
                  mx: "auto",
                  mb: 2,
                  background:
                    "linear-gradient(135deg,#5f72ff,#9b23ea)"
                }}
              >
                {userData?.prenom?.charAt(0)}
                {userData?.nom?.charAt(0)}
              </Avatar>

              <Typography variant="h6">
                {userData?.prenom} {userData?.nom}
              </Typography>

              <Typography color="text.secondary">
                {userData?.email}
              </Typography>

              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
              >
                {userData?.role || "Admin"}
              </Button>

              <Divider sx={{ my: 3 }} />

              <Typography>
                <b>Restaurant :</b> Restaurant Principal
              </Typography>

              <Typography mt={1}>
                <b>Membre depuis :</b> 01 Jan 2026
              </Typography>

              <Button
                fullWidth
                color="error"
                variant="outlined"
                startIcon={<ExitToAppOutlined />}
                sx={{ mt: 3 }}
                onClick={handleLogout}
              >
                Se Déconnecter
              </Button>

            </CardContent>
          </Card>

        </Grid>

        {/* RIGHT PANEL */}

        <Grid item xs={12} md={8}>

          <Paper sx={{ borderRadius: 4, p: 3 }}>

            <Tabs value={tab} onChange={handleTabChange}>

              <Tab label="Informations" icon={<PersonOutline />} />

              <Tab label="Sécurité" icon={<LockOutlined />} />

              <Tab label="Notifications" icon={<NotificationsNone />} />

            </Tabs>

            <Divider sx={{ my: 3 }} />

            {/* TAB 1 */}

            {tab === 0 && (

              <Grid container spacing={3}>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    name="num"
                    value={formData.num}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    disabled
                    label="Email"
                    value={userData?.email}
                  />
                </Grid>

                <Grid item xs={12}>

                  <Button
                    variant="contained"
                    startIcon={<SaveOutlined />}
                    onClick={handleUpdate}
                  >
                    Enregistrer les Modifications
                  </Button>

                </Grid>

              </Grid>
            )}

            {/* TAB 2 */}

            {tab === 1 && (

              <Grid container spacing={3}>

                <Grid item xs={12}>

                  <TextField
                    fullWidth
                    label="Mot de passe actuel"
                    name="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                  />

                </Grid>

                <Grid item xs={12}>

                  <TextField
                    fullWidth
                    label="Nouveau mot de passe"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                  />

                </Grid>

                <Grid item xs={12}>

                  <TextField
                    fullWidth
                    label="Confirmer mot de passe"
                    name="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                  />

                </Grid>

                <Grid item xs={12}>

                  <Button
                    variant="contained"
                    onClick={handleUpdatePassword}
                  >
                    Mettre à Jour le Mot de Passe
                  </Button>

                </Grid>

              </Grid>
            )}

            {/* TAB 3 */}

            {tab === 2 && (

              <Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography>Notifications Email</Typography>
                  <Switch defaultChecked />
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography>Alertes de Stock</Typography>
                  <Switch defaultChecked />
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography>Rapports Quotidiens</Typography>
                  <Switch />
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography>Paiements en Retard</Typography>
                  <Switch defaultChecked />
                </Box>

              </Box>
            )}

          </Paper>

        </Grid>

      </Grid>

    </Box>
  );
};

export default UserProfile;